import lodash from 'lodash'
import React, { useEffect, useMemo } from 'react'

import Address from '../../../../../1stlog-ui/src/components/Address'
import { Col, FormRow } from '../../../../../1stlog-ui/src/components/Bootstrap'
import Checkbox from '../../../../../1stlog-ui/src/components/Checkbox'
import Field, {
  FieldPrefix,
  useField,
} from '../../../../../1stlog-ui/src/components/Field'
import useFieldState from '../../../../../1stlog-ui/src/components/Field/useFieldState'
import useFieldValue from '../../../../../1stlog-ui/src/components/Field/useFieldValue'
import Input from '../../../../../1stlog-ui/src/components/Input'
import { usePrevious } from '../../../../../1stlog-ui/src/hooks'
import AddressPickerAdvanced from '../../../AddressPickerAdvanced'
import AddressPickerAdvancedReadonly from '../../../AddressPickerAdvanced/Readonly'
import AddressSwitch from '../../components/AddressSwitch'
import OrderEntryCard, {
  IOrderEntryCardProps,
} from '../../components/OrderEntryCard'
import { useProductSettings } from '../../Products'
import useOrderEntry from '../../useOrderEntry'
import { getAddressPickerLabels } from '../../utils/getAddressPickerLabels'
import useCustoms from '../Customs/useCustoms'
import ReadonlyAddressCache from '../ReadonlyAddressCache'

interface TInvoiceAddressProps
  extends Omit<IOrderEntryCardProps, 'title' | 'children'> {
  readonly?: boolean
}

const InvoiceAddress = (props: TInvoiceAddressProps): React.ReactElement => {
  const { readonly, ...rest } = props

  const { preset, currentCustomer, depots } = useOrderEntry()

  const { showInvoiceAddress, allowInvoiceAddressEdit } = preset

  const { invoiceAddresses = [], customerInvoiceAddress } =
    currentCustomer || {}

  const addresses = useMemo(() => {
    return lodash
      .concat([customerInvoiceAddress], invoiceAddresses)
      .filter(Boolean)
  }, [currentCustomer])

  const {
    overwriteInvoiceAddressAllowed: overwriteAllowed,
    isFreightCollectCheckAllowed,
    isFreightCollectReferenceHidden,
  } = useProductSettings()

  const [isEdit, setIsEdit] = useField('isEdit', { asState: true })

  const [address, setAddress] = useField('address', { asState: true })

  useEffect(() => {
    setTimeout(() => setIsEdit(false), 0)
  }, [])

  const previousOverwriteAllowed = usePrevious(overwriteAllowed)
  useEffect(() => {
    // overwrite not allowed anymore, set isEdit to false
    if (previousOverwriteAllowed && !overwriteAllowed) {
      setTimeout(() => setIsEdit(false), 0)
    }
  }, [overwriteAllowed])

  // read customerAddress in internal mode
  const customerAddress = useField('customer.address', { ignorePrefix: true })
  useEffect(() => {
    if (!isEdit && !currentCustomer) {
      setAddress(customerAddress)
    }
  }, [customerAddress, isEdit, currentCustomer])

  // Unfrei
  const [freightCollect, setFreightCollect] =
    useFieldState<boolean>('freightCollect')
  const deliveryAddress: any = useFieldValue('delivery.address', {
    ignorePrefix: true,
  })

  const { isCustomsRequired } = useCustoms()

  const isOwnDeliveryAddress = useFieldValue('delivery.isOwnDeliveryAddress', {
    ignorePrefix: true,
  })

  const isUnfreiAllowed = useMemo(
    () =>
      isFreightCollectCheckAllowed &&
      !isCustomsRequired &&
      !isOwnDeliveryAddress,
    [isFreightCollectCheckAllowed, isCustomsRequired, isOwnDeliveryAddress],
  )

  useEffect(() => {
    if (!isUnfreiAllowed && freightCollect) {
      setTimeout(() => setFreightCollect(false), 0)
    }
  }, [isUnfreiAllowed, freightCollect])

  return (
    <>
      <ReadonlyAddressCache />
      {showInvoiceAddress && (
        <OrderEntryCard
          {...rest}
          title={i18n.t('invoice_address', 'Rechnungsadresse')}
        >
          {isUnfreiAllowed && (
            <div className="">
              <FormRow>
                <Col>
                  <Field name="freightCollect" noMargin>
                    <Checkbox
                      label={i18n.t('unfree', 'Unfrei')}
                      disabled={isEdit}
                    />
                  </Field>
                </Col>
              </FormRow>
              {freightCollect && (
                <div className="mt-1">
                  <FormRow>
                    <Col>
                      <div style={{ display: 'flex' }}>
                        <Address address={deliveryAddress} />
                      </div>
                    </Col>
                  </FormRow>
                  {!isFreightCollectReferenceHidden && (
                    <FormRow>
                      <Col>
                        <Field
                          name="freightCollectReference"
                          label={i18n.t(
                            'freight_collect_reference',
                            'Rechnungsreferenz',
                          )}
                        >
                          <Input />
                        </Field>
                      </Col>
                    </FormRow>
                  )}
                </div>
              )}
            </div>
          )}
          {!freightCollect && (
            <>
              <div className="mt-1">
                {!readonly && (overwriteAllowed || allowInvoiceAddressEdit) && (
                  <Field name="isEdit">
                    <Checkbox
                      label={i18n.t(
                        'different_invoice_address',
                        'Abweichende Rechnungsadresse',
                      )}
                      disabled={freightCollect}
                    />
                  </Field>
                )}
                {!readonly && !isEdit && lodash.size(invoiceAddresses) > 0 && (
                  <AddressSwitch addresses={addresses} />
                )}
                <FieldPrefix prefix="address">
                  {!readonly && isEdit ? (
                    <AddressPickerAdvanced labels={getAddressPickerLabels()} />
                  ) : (
                    <AddressPickerAdvancedReadonly
                      emptyText={i18n.t(
                        'please_enter_customer_address_first',
                        'Geben Sie zuerst eine Kundenadresse an',
                      )}
                    />
                  )}
                </FieldPrefix>
              </div>
            </>
          )}
        </OrderEntryCard>
      )}
    </>
  )
}

export default InvoiceAddress
